import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * useConfirm — replaces `window.confirm()` with a Shadcn AlertDialog so we honour
 * Prompt 13.5 Error Prevention + Consistency principles.
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   const onArchive = async () => {
 *       if (await confirm({ title: "Archive recipe?", description: "..." })) { ... }
 *   };
 *   // somewhere in your JSX: <ConfirmDialog />
 */
export function useConfirm() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    // null labels → translated defaults at render time.
    const [opts, setOpts] = useState({
        title: null, description: "",
        confirmLabel: null, cancelLabel: null,
        destructive: false,
    });
    const resolverRef = useRef(null);

    const confirm = useCallback((options = {}) => {
        setOpts((prev) => ({ ...prev, ...options }));
        setOpen(true);
        return new Promise((resolve) => { resolverRef.current = resolve; });
    }, []);

    const finish = (value) => {
        setOpen(false);
        resolverRef.current?.(value);
        resolverRef.current = null;
    };

    const ConfirmDialog = () => (
        <AlertDialog open={open} onOpenChange={(v) => { if (!v) finish(false); }}>
            <AlertDialogContent data-testid="confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{opts.title ?? t("confirm.areYouSure", "Are you sure?")}</AlertDialogTitle>
                    {opts.description && (
                        <AlertDialogDescription>{opts.description}</AlertDialogDescription>
                    )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="confirm-cancel" onClick={() => finish(false)}>
                        {opts.cancelLabel ?? t("common.cancel", "Cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-testid="confirm-accept"
                        onClick={() => finish(true)}
                        className={opts.destructive
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : undefined}
                    >
                        {opts.confirmLabel ?? t("common.confirm", "Confirm")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return { confirm, ConfirmDialog };
}
